import { Stack, Typography, Box, Divider } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { convertPxToRem } from "@/utils";
import CircledBackButton from "@/features/Common/components/CircledBackButton";
import IconLoader from "@/features/Common/components/IconLoader";
import theme, { panelStyle } from "@/theme";
import {
  MemberProductPaymentRatesChargeCode,
  MemberProductPaymentRatesItem,
} from "@/services/core-api-adapter";

interface ThirdPartyConsultationTypeSelectionProps {
  productPaymentRates: MemberProductPaymentRatesItem[];
  onDoctorConsultClick: () => void;
  onNurseConsultClick: () => void;
  onLaboratoryServicesClick: () => void;
  onPharmacyServicesClick: () => void;
  onClinicAppointmentClick: () => void;
}

const consultationPanelInfo: {
  [key: string]: {
    title: any;
    subtitle: any;
    amount: any;
    width: number;
    priceDependent?: boolean;
    isFree?: boolean;
    shouldDisplayPrice?: boolean;
  };
} = {
  REMOTE_DOCTOR_CONSULTATIONS: {
    title: "ThirdPartyConsultationTypeSelection.doctorConsult.title",
    subtitle: "ThirdPartyConsultationTypeSelection.doctorConsult.subtitle",
    amount: "ThirdPartyConsultationTypeSelection.doctorConsult.amount",
    width: 120,
    shouldDisplayPrice: true,
  },
  NURSE_CHAT: {
    title: "ThirdPartyConsultationTypeSelection.nurseConsult.title",
    subtitle: "ThirdPartyConsultationTypeSelection.nurseConsult.subtitle",
    amount: "ThirdPartyConsultationTypeSelection.nurseConsult.amount",
    isFree: true,
    width: 90,
    shouldDisplayPrice: true,
  },
  LABORATORY: {
    title: "ThirdPartyConsultationTypeSelection.laboratoryServices.title",
    subtitle: "ThirdPartyConsultationTypeSelection.laboratoryServices.subtitle",
    amount: "ThirdPartyConsultationTypeSelection.laboratoryServices.amount",
    priceDependent: true,
    width: 150,
    shouldDisplayPrice: false,
  },
  PHARMACY: {
    title: "ThirdPartyConsultationTypeSelection.pharmacyServices.title",
    subtitle: "ThirdPartyConsultationTypeSelection.pharmacyServices.subtitle",
    amount: "ThirdPartyConsultationTypeSelection.pharmacyServices.amount",
    priceDependent: true,
    width: 150,
    shouldDisplayPrice: false,
  },
  CLINIC: {
    title: "ThirdPartyConsultationTypeSelection.clinicAppointment.title",
    subtitle: "ThirdPartyConsultationTypeSelection.clinicAppointment.subtitle",
    amount: "ThirdPartyConsultationTypeSelection.clinicAppointment.amount",
    width: 120,
    shouldDisplayPrice: true,
  },
};

function ConsultationPanel({
  service,
  onClick,
}: {
  service: MemberProductPaymentRatesItem | null;
  onClick: () => void;
}) {
  const { t } = useTranslation();

  const transformedService = service?.chargeCode.toUpperCase() || "";

  const serviceData =
    transformedService in consultationPanelInfo
      ? consultationPanelInfo[transformedService]
      : null;

  return (
    <Stack
      component="button"
      sx={{
        cursor: "pointer",
        border: "none",
      }}
      aria-label={t(serviceData?.title)}
      color="neutral"
      onClick={onClick}
    >
      <Box padding={4} textAlign="left" sx={{ ...panelStyle, width: "100%" }}>
        <Stack
          direction="row"
          width="100%"
          justifyContent="space-between"
          alignItems="center"
          mt={2}
        >
          <Stack spacing={2}>
            <Stack spacing={1}>
              <Typography
                variant="body2"
                color="neutral.light"
                fontWeight={600}
              >
                {t(serviceData?.subtitle)}
              </Typography>
              <Typography variant="h3" fontWeight={600}>
                {t(serviceData?.title)}
              </Typography>
            </Stack>
            {serviceData?.shouldDisplayPrice && (
              <Box
                textAlign="center"
                sx={{
                  background: (theme) => theme.palette.primary.main,
                  width: serviceData?.width,
                  borderRadius: 2,
                  padding: 1,
                }}
              >
                <Typography color="primary.contrastText" fontWeight={600}>
                  {serviceData?.isFree ? (
                    t("common.free")
                  ) : (
                    <>
                      {serviceData?.priceDependent
                        ? t("common.priceFrom", {
                            amount: service?.amountFormatted,
                          })
                        : service?.amountFormatted}
                    </>
                  )}
                </Typography>
              </Box>
            )}
            {serviceData?.priceDependent && (
              <Typography color="neutral.500" variant="body2">
                {t("ThirdPartyConsultationTypeSelection.priceDependentSubText")}
              </Typography>
            )}
          </Stack>
          <IconLoader
            sx={{ fontSize: 30 }}
            color="primary"
            icon="ChevronNextIcon"
          />
        </Stack>
      </Box>
    </Stack>
  );
}

export function ThirdPartyConsultationTypeSelection({
  productPaymentRates,
  onDoctorConsultClick,
  onNurseConsultClick,
  onLaboratoryServicesClick,
  onPharmacyServicesClick,
  onClinicAppointmentClick,
}: ThirdPartyConsultationTypeSelectionProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  function onBackButtonClick() {
    navigate(-1);
  }

  function getServiceFromProductPaymentRatesList(
    chargeCode: MemberProductPaymentRatesChargeCode
  ) {
    return (
      productPaymentRates?.find((item) => item.chargeCode === chargeCode) ||
      null
    );
  }

  function isServiceAvailable(chargeCode: MemberProductPaymentRatesChargeCode) {
    return productPaymentRates.some((item) => item.chargeCode === chargeCode);
  }

  const hasAdditionalServices =
    isServiceAvailable(MemberProductPaymentRatesChargeCode.LABORATORY) ||
    isServiceAvailable(MemberProductPaymentRatesChargeCode.PHARMACY) ||
    isServiceAvailable(MemberProductPaymentRatesChargeCode.CLINIC);

  return (
    <Stack
      color="neutral.dark"
      sx={{
        minHeight: "100%",
        justifyContent: "space-between",
        flex: 1,
      }}
    >
      <Stack p={2} spacing={2}>
        <Stack>
          <CircledBackButton showLabel={true} onClick={onBackButtonClick} />
        </Stack>
        <Stack>
          <IconLoader
            icon="ChatHeartIcon"
            color="primary"
            sx={{
              fontSize: convertPxToRem(56),
              alignSelf: "center",
            }}
          />
          <Typography textAlign="center" variant="h1" color="inherit">
            {t("ConsultationTypeSelection.title")}
          </Typography>

          <Stack spacing={6} py={4}>
            <Stack spacing={4}>
              {isServiceAvailable(
                MemberProductPaymentRatesChargeCode.NURSE_CHAT
              ) && (
                <ConsultationPanel
                  service={getServiceFromProductPaymentRatesList(
                    MemberProductPaymentRatesChargeCode.NURSE_CHAT
                  )}
                  onClick={onNurseConsultClick}
                />
              )}

              {isServiceAvailable(
                MemberProductPaymentRatesChargeCode.REMOTE_DOCTOR_CONSULTATIONS
              ) && (
                <ConsultationPanel
                  service={getServiceFromProductPaymentRatesList(
                    MemberProductPaymentRatesChargeCode.REMOTE_DOCTOR_CONSULTATIONS
                  )}
                  onClick={onDoctorConsultClick}
                />
              )}
            </Stack>

            {hasAdditionalServices && (
              <>
                <Stack display="flex" position="relative">
                  <Divider sx={{ borderWidth: ".5px", bgcolor: "primary" }} />
                  <Typography
                    variant="body2"
                    fontWeight={600}
                    fontSize={convertPxToRem(10)}
                    sx={{
                      top: "50%",
                      left: "50%",
                      textAlign: "center",
                      transform: "translate(-50%, -50%)",
                      position: "absolute",
                      zIndex: 1,
                      p: 0.5,
                      borderRadius: theme.shape.borderRadius,
                      borderColor: (theme) => theme.palette.primary.main,
                      color: theme.palette.primary.main,
                      borderWidth: 1,
                      borderStyle: "solid",
                      background: () => `${theme.palette.accent2[100]}`,
                      width: 35,
                    }}
                  >
                    {t("ActiveConsultations.dividerLabel")}
                  </Typography>
                </Stack>
                <Stack spacing={4}>
                  <Stack alignItems="center">
                    <Typography variant="h2" fontWeight={600}>
                      {t(
                        "ThirdPartyConsultationTypeSelection.selectAnotherService"
                      )}
                    </Typography>
                  </Stack>

                  {isServiceAvailable(
                    MemberProductPaymentRatesChargeCode.LABORATORY
                  ) && (
                    <ConsultationPanel
                      service={getServiceFromProductPaymentRatesList(
                        MemberProductPaymentRatesChargeCode.LABORATORY
                      )}
                      onClick={onLaboratoryServicesClick}
                    />
                  )}

                  {isServiceAvailable(
                    MemberProductPaymentRatesChargeCode.PHARMACY
                  ) && (
                    <ConsultationPanel
                      service={getServiceFromProductPaymentRatesList(
                        MemberProductPaymentRatesChargeCode.PHARMACY
                      )}
                      onClick={onPharmacyServicesClick}
                    />
                  )}

                  {isServiceAvailable(
                    MemberProductPaymentRatesChargeCode.CLINIC
                  ) && (
                    <ConsultationPanel
                      service={getServiceFromProductPaymentRatesList(
                        MemberProductPaymentRatesChargeCode.CLINIC
                      )}
                      onClick={onClinicAppointmentClick}
                    />
                  )}
                </Stack>
              </>
            )}
          </Stack>
        </Stack>
      </Stack>
    </Stack>
  );
}
